import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Music4, Trophy, Swords } from "lucide-react";
import { SITE } from "../data/site";

const PERKS = [
  { icon: Sparkles, title: "Confidence", desc: "Performance-first teaching that builds presence on and off stage." },
  { icon: Music4, title: "Creativity", desc: "Freestyle, rhythm and self-expression woven into every session." },
  { icon: Trophy, title: "Showcases", desc: "Termly performances at real venues — not just studio recitals." },
  { icon: Swords, title: "Competitions", desc: "Junior battles and inter-school comps — safe, supported and fun." },
];

export const Kids = () => {
  return (
    <section
      id="kids"
      data-focus="kids-home"
      data-testid="kids-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand-purple/25 blur-[150px]" />
      <div className="absolute bottom-0 -left-32 w-[400px] h-[400px] rounded-full bg-brand-yellow/10 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-brand-yellow" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">03 · USDD Kids</span>
          </div>

          <img src={SITE.kidsLogo} alt="USDD Kids" className="h-28 w-auto mb-6 drop-shadow-[0_0_24px_rgba(157,78,221,0.6)]" />

          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter leading-[0.95]">
            Big energy.
            <br />
            <span className="text-brand-purpleLight">Tiny dancers.</span>
          </h2>

          <p className="mt-6 text-lg text-zinc-300 max-w-lg leading-relaxed">
            USDD Kids is our junior and youth programme for ages 6–17. Real
            street-dance training delivered the right way — safe, structured
            and seriously fun.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {PERKS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`kids-perk-${i}`}
                className="p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-brand-yellow/40 transition-all hover:-translate-y-1"
              >
                <p.icon className="text-brand-yellow mb-3" size={22} />
                <div className="font-display font-bold text-white text-base">{p.title}</div>
                <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{p.desc}</div>
              </div>
            ))}
          </div>

          <Link
            to="/kids-registration"
            data-testid="kids-enrol-btn"
            className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-black font-bold uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-105 transition-all glow-yellow"
          >
            Enrol your kid
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative"
        >
          <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/5]">
            <img src={SITE.kidsShow} alt="USDD Kids on stage" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-2">Live showcase · 2025</div>
              <div className="font-display font-black uppercase text-2xl text-white">The next generation. Live on stage.</div>
            </div>
          </div>
          <div className="absolute -top-6 -right-6 hidden md:block px-5 py-3 rounded-2xl bg-brand-yellow text-black font-display font-black uppercase text-sm rotate-3 glow-yellow">
            Ages 6–17
          </div>
        </motion.div>
      </div>
    </section>
  );
};
